





































import {
  defineComponent,
  onMounted,
  onUnmounted,
  ref,
  Ref,
  watch,
} from '@vue/composition-api';
import { useRouter, useFormat } from '@ideavivat/vivat-2';

import useEcho from '@/useEcho';
import useAuth from '@/useAuth';

import AnswerBubble from '@/components/Question/AnswerBubble.vue';
import CommonModalWithPreset from '@/components/CommonModalWithPreset.vue';

import {
  getUserWithQuestions,
  answerQuestion,
  deleteQuestionAnswer,
} from '@/api/index';

export interface Question {
  id: number;
  message: string;
  isAdmin: boolean;
  time: string;
  isSelf: boolean;
}

const ChatBox = defineComponent({
  components: {
    AnswerBubble,
    CommonModalWithPreset,
  },
  setup(props: any, { root, emit }) {
    const { route } = useRouter();
    const { pusher } = useEcho();
    const { getMe } = useAuth();
    const me = ref({
      id: -1,
    });

    const loading = ref(false);
    const pagination = ref({
      // eslint-disable-next-line
      current_page: 1,
      // eslint-disable-next-line
      last_page: 1,
      total: 0,
    });
    const page = ref(1);
    const sort = ref({ field: 'created_at', order: 'DESC' });

    const messages: Ref<Question[]> = ref([]);
    async function callGetQuestion() {
      root.$store.commit('loading', true);
      loading.value = true;
      const result = await getUserWithQuestions(
        route.value.params.userId,
        page.value,
        50,
        sort.value,
      );
      return result;
    }

    const messagesArea: Ref<HTMLElement | null> = ref(null);
    function scrollToBottom(smooth = true) {
      (messagesArea.value as HTMLElement).scrollTo({
        top: (messagesArea.value as HTMLElement).scrollHeight,
        behavior: smooth ? 'smooth' : 'auto',
      });
    }

    function mapAPIDataToQuestion(message: any) {
      const time = useFormat.date(message.created_at).displayShortThaiDateTime();
      return {
        id: message.id,
        message: message.message,
        isAdmin: message.from_admin,
        time,
        isSelf: message.answer_by_user_id === (me.value).id,
      };
    }

    async function callGetAnotherQuestions() {
      const { data, ...paginatorData } = await callGetQuestion();
      if (!data) return;
      const temporaryMessages = data.map(mapAPIDataToQuestion).sort((a: any, b: any) => a.id - b.id);
      messages.value = [...temporaryMessages, ...messages.value];
      pagination.value = paginatorData;
      root.$store.commit('loading', false);
    }

    async function callGetQuestionWithRefreshData() {
      page.value = 1;
      const { data, ...paginatorData } = await callGetQuestion();
      messages.value = [];

      if (data && Array.isArray(data)) messages.value = data.map(mapAPIDataToQuestion).sort((a: any, b: any) => a.id - b.id);

      pagination.value = paginatorData;
      await scrollToBottom(false);
      root.$store.commit('loading', false);
    }

    const textMessage = ref('');
    const readyToSubmitMessage = ref(true);

    async function scrollMessagesAreaHandler({ target: { scrollTop } }: any) {
      // eslint-disable-next-line
      if (scrollTop === 0 && pagination.value.current_page < pagination.value.last_page) {
        page.value += 1;
        await callGetAnotherQuestions();
      }
    }

    async function processReceiveChatEvent(data: any) {
      const time = (new Date(data.created_at)).toTimeString().substring(0, 5);
      emit('hasUpdate');
      const message = {
        id: data.id,
        message: data.message,
        isAdmin: data.from_admin,
        time,
        isSelf: data.answer_by_user_id === (me.value).id,
      };
      await messages.value.push(message);
      await scrollToBottom();
    }

    async function submit() {
      if (textMessage.value === '') return '';
      const result = await answerQuestion(
        textMessage.value,
        route.value.params.liveId,
        route.value.params.userId,
      );
      textMessage.value = '';
      await processReceiveChatEvent(result);
      return '';
    }

    async function deleteChatMessage(id: number | string) {
      await deleteQuestionAnswer(id);
    }

    async function processDeleteQuestionEvent(data: any) {
      if ((messages.value).length > 0) {
        const index = (messages.value).findIndex((message: Question) => message.id === data.id);
        if (index > -1) {
          (messages.value).splice(index, 1);
        }
      }
    }

    onMounted(async () => {
      me.value = await getMe();
      await callGetQuestionWithRefreshData();

      if (pusher) {
        pusher.subscribe('private-admin-questions')
          .bind('UserSubmittedQuestion', async (data: any) => {
            // eslint-disable-next-line
            if (data.question.user_id == route.value.params.userId) await processReceiveChatEvent(data.question);
          })
          .bind('QuestionDeletedAdmin', async (data: any) => {
            await processDeleteQuestionEvent(data.question);
          })
          .bind('QuestionClearAdmin', (data: any) => {
            // eslint-disable-next-line
            if (data.session.id == route.value.params.liveId && data.user.id == route.value.params.userId) messages.value = [];
          });
      }
    });

    watch(() => route.value.params.userId, (newVal, oldVal) => {
      if (newVal && newVal !== oldVal && newVal > -1 && oldVal > -1) {
        if (pusher) {
          pusher.unsubscribe(`private-user.${oldVal}.questions.${route.value.params.liveId}`);
          pusher.subscribe(`private-user.${newVal}.questions.${route.value.params.liveId}`).bind('QuestionUpdated', async (data: any) => {
            // eslint-disable-next-line
            if (data.question.user_id == route.value.params.userId && me.value.id != data.question.answer_by_user_id) await processReceiveChatEvent(data.question);
          });
        }
      }
    });

    onUnmounted(async () => {
      pusher.unsubscribe('private-admin-questions');
      pusher.unsubscribe(`private-user.${route.value.params.userId}.questions.${route.value.params.liveId}`);
    });

    function checkIsMessageHaveSameSender(index: number) {
      if (index === 0) return false;
      if ((messages.value)[index].isAdmin) {
        if (
          (messages.value)[index].time === (messages.value)[index - 1].time
          && (messages.value)[index].isAdmin && (messages.value)[index - 1].isAdmin
        ) return true;
      } else if (!(messages.value)[index].isAdmin) {
        if (
          (messages.value)[index].time === (messages.value)[index - 1].time
          && !(messages.value)[index - 1].isAdmin
        ) return true;
      }
      return false;
    }

    // use to refetch Chat Message when change user
    watch(() => route.value.params.userId, async (newVal) => {
      if (newVal) await callGetQuestionWithRefreshData();
    });

    return {
      messages,
      textMessage,
      submit,
      me,
      readyToSubmitMessage,
      deleteChatMessage,

      messagesArea,
      scrollMessagesAreaHandler,

      checkIsMessageHaveSameSender,
    };
  },
});

export default ChatBox;
